import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { checkResetKey, resetPassword } from "@/auth/api";
import type { ResetKeyCheck } from "@/auth/api";
import { AuthError, AuthScreen } from "@/auth/AuthScreen";
import { Button, FormField, Input } from "@/components/ui";
import type { LoginState } from "./Login";

/**
 * Where the link in the reset email lands (`HEADLESS_FRONTEND_URLS`).
 *
 * The key is checked before the form is drawn. A link opened twice, or a week
 * late, should say so at once — not after the seller has thought up a new
 * password and typed it twice.
 */
export function PasswordReset() {
  const { t } = useTranslation();
  const { key = "" } = useParams();
  const navigate = useNavigate();

  const [check, setCheck] = useState<ResetKeyCheck | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void checkResetKey(key).then((result) => {
      if (!cancelled) setCheck(result);
    });
    return () => {
      cancelled = true;
    };
  }, [key, attempt]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== repeat) {
      setError(t("auth.reset.mismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    const result = await resetPassword(key, password);
    if (result.ok) {
      // `replace`: Back from the sign-in screen must not return to a link
      // that has just been spent.
      const state: LoginState = { passwordChanged: true };
      void navigate("/entrar", { replace: true, state });
      return;
    }
    setBusy(false);
    if (result.invalidKey) setCheck({ state: "invalid" });
    else setError(result.message);
  }

  const backToLogin = (
    <Link to="/entrar" className="btn btn-ghost">
      {t("auth.recovery.back")}
    </Link>
  );

  if (check === null) {
    return (
      <AuthScreen title={t("auth.reset.title")}>
        <p role="status" className="mb-0 text-sm text-mute-700">
          {t("auth.reset.checking")}
        </p>
      </AuthScreen>
    );
  }

  if (check.state === "invalid") {
    return (
      <AuthScreen title={t("auth.reset.title")} footer={backToLogin}>
        <AuthError>{t("auth.reset.invalid")}</AuthError>
        <Link to="/recuperar" className="btn btn-primary btn-block justify-center">
          {t("auth.reset.requestNew")}
        </Link>
      </AuthScreen>
    );
  }

  if (check.state === "unreachable") {
    return (
      <AuthScreen title={t("auth.reset.title")} footer={backToLogin}>
        <AuthError>{t("auth.errors.generic")}</AuthError>
        <Button
          block
          onClick={() => {
            setCheck(null);
            setAttempt((n) => n + 1);
          }}
        >
          {t("common.retry")}
        </Button>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen title={t("auth.reset.title")} footer={backToLogin}>
      {check.account ? (
        <p className="mb-4 text-sm text-mute-700">
          {t("auth.reset.account", { account: check.account })}
        </p>
      ) : null}
      {error ? <AuthError>{error}</AuthError> : null}
      <form onSubmit={submit} className="space-y-4">
        {/* Not for the seller: a password manager needs to see whose password
            this is, or it files the new one under no account at all. */}
        <input
          type="text"
          name="username"
          autoComplete="username"
          value={check.account}
          readOnly
          hidden
        />
        <FormField label={t("auth.reset.password")} hint={t("auth.reset.hint")}>
          {(id) => (
            <Input
              id={id}
              type="password"
              name="password"
              autoComplete="new-password"
              autoFocus
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </FormField>
        <FormField label={t("auth.reset.repeat")}>
          {(id) => (
            <Input
              id={id}
              type="password"
              name="repeat"
              autoComplete="new-password"
              required
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          )}
        </FormField>
        <Button variant="primary" type="submit" block disabled={busy}>
          {busy ? t("auth.reset.submitting") : t("auth.reset.submit")}
        </Button>
      </form>
    </AuthScreen>
  );
}
